import React from 'react';
import { View, Text, Modal, Pressable, Platform } from 'react-native';
import { useStyles } from '../../hooks/useStyles';
import { useResponsive } from '../../shared/utils/responsive';
import { CommentThread as CommentThreadType } from '../../shared/types/comments';
import { ChatThread } from './ChatThread';
import { addColorOpacity } from '../../shared/utils/styles';
import { Ionicons } from '@expo/vector-icons';

interface DiscussionModalProps {
  visible: boolean;
  thread: CommentThreadType | null;
  onClose: () => void;
  onOpenFullPage?: () => void;
}

/**
 * Modal wrapper for viewing and interacting with a discussion thread
 * in a chat-widget style without navigating away from the current page.
 */
export function DiscussionModal({ 
  visible, 
  thread, 
  onClose, 
  onOpenFullPage 
}: DiscussionModalProps) {
  const styles = useStyles();
  const { isMobile, isSmallMobile, isTablet, isDesktop } = useResponsive();

  if (!thread) return null;

  // Responsive modal sizing
  const getModalWidth = () => {
    if (isSmallMobile) return '98%';
    if (isMobile) return '95%';
    if (isTablet) return '80%';
    return 650; // Desktop
  };

  const getModalHeight = () => {
    if (isSmallMobile) return '95%';
    if (isMobile) return '90%';
    if (isTablet) return '85%';
    return '80%';
  };

  const getModalMaxHeight = () => {
    if (isSmallMobile) return 700;
    if (isMobile) return 750;
    return 800;
  };

  // Get display title based on context
  const getTitle = () => {
    if (thread.title) return thread.title;
    
    switch (thread.context_type) {
      case 'portfolio':
        return 'Portfolio Discussion';
      case 'property':
        return 'Property Discussion';
      case 'mortgage_calculation':
        return 'Mortgage Discussion';
      case 'financial_entry':
        return 'Financial Entry Discussion';
      case 'due_diligence_item':
        return 'Due Diligence Discussion';
      case 'survey_item':
        return 'Survey Discussion';
      default:
        return 'Discussion';
    }
  };

  const headerPadding = isSmallMobile ? styles.utils.spacing.sm : styles.utils.spacing.md;
  const titleSize = isSmallMobile ? 16 : isMobile ? 18 : 20;

  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={onClose}
    >
      <Pressable 
        style={{
          flex: 1,
          justifyContent: 'center',
          alignItems: 'center',
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          padding: isSmallMobile ? styles.utils.spacing.xs : styles.utils.spacing.md,
        }}
        onPress={onClose}
      >
        <Pressable 
          style={{
            backgroundColor: styles.tokens.color.surface,
            borderRadius: styles.utils.radius.lg,
            width: getModalWidth() as any,
            height: getModalHeight() as any,
            maxHeight: getModalMaxHeight(),
            maxWidth: isDesktop ? 700 : undefined,
            overflow: 'hidden',
            borderWidth: 1,
            borderColor: styles.tokens.color.border,
            ...(Platform.OS === 'web' && {
              boxShadow: '0 4px 24px rgba(0,0,0,0.2)',
            }),
          }}
          onPress={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <View style={{
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            paddingHorizontal: headerPadding,
            paddingVertical: headerPadding,
            borderBottomWidth: 1,
            borderBottomColor: styles.tokens.color.border,
            backgroundColor: addColorOpacity(styles.tokens.color.primary, 0.03),
          }}>
            <View style={{ flex: 1, minWidth: 0 }}>
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                <Ionicons 
                  name="chatbubbles" 
                  size={isSmallMobile ? 18 : 20} 
                  color={styles.tokens.color.primary} 
                />
                <Text 
                  style={{
                    fontSize: titleSize,
                    fontWeight: '600',
                    color: styles.tokens.color.onSurface,
                    flex: 1,
                  }}
                  numberOfLines={1}
                >
                  {getTitle()}
                </Text>
              </View>
              
              {/* Context info */}
              <Text style={{
                fontSize: isSmallMobile ? 11 : 12,
                color: addColorOpacity(styles.tokens.color.onSurface, 0.5),
                marginTop: 2,
                marginLeft: isSmallMobile ? 26 : 28,
              }}>
                {thread.comment_count || 0} messages
                {thread.context_name && ` • ${thread.context_name}`}
              </Text>
            </View>

            {/* Close button */}
            <Pressable
              onPress={onClose}
              style={{
                width: isSmallMobile ? 32 : 36,
                height: isSmallMobile ? 32 : 36,
                borderRadius: isSmallMobile ? 16 : 18,
                backgroundColor: addColorOpacity(styles.tokens.color.onSurface, 0.08),
                alignItems: 'center',
                justifyContent: 'center',
                marginLeft: styles.utils.spacing.sm,
              }}
              hitSlop={8}
            >
              <Ionicons 
                name="close" 
                size={isSmallMobile ? 20 : 22} 
                color={addColorOpacity(styles.tokens.color.onSurface, 0.7)} 
              />
            </Pressable>
          </View>

          {/* Chat thread */}
          <View style={{ flex: 1, minHeight: 0 }}>
            <ChatThread
              thread={thread}
              onOpenFullPage={onOpenFullPage}
              maxHeight={undefined} // Let it fill available space
            />
          </View>
        </Pressable>
      </Pressable>
    </Modal>
  );
}

